import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { FiPhone, FiMapPin, FiGlobe, FiInstagram } from 'react-icons/fi';
import PrivacyPolicy from './PrivacyPolicy';

const NAV = [
  { key:'experiences', id:'activities'  },
  { key:'food',        id:'food'        },
  { key:'access',      id:'access'      },
  { key:'faq',         id:'faq'         },
  { key:'reservation', id:'reservation' },
];


const TREES = [
  { e:'🌲', size:46, dur:6,   delay:0    },
  { e:'🌳', size:36, dur:5,   delay:0.5  },
  { e:'🌲', size:50, dur:7,   delay:1    },
  { e:'🌿', size:32, dur:5.5, delay:0.3  },
  { e:'🌳', size:42, dur:6.5, delay:1.5  },
  { e:'🌲', size:38, dur:5,   delay:0.8  },
  { e:'🌲', size:48, dur:7,   delay:0.2  },
  { e:'🌳', size:30, dur:6,   delay:1.2  },
];

function ColHead({ children }) {
  return (
    <div className="flex items-center gap-2 mb-5">
      <span className="text-[9px] font-extrabold tracking-[.28em] uppercase text-white/35 whitespace-nowrap">
        {children}
      </span>
      <div className="flex-1 h-px bg-white/[.08]" />
    </div>
  );
}

function ContactItem({ icon: Icon, iconCls, label, href, children }) {
  const Tag = href ? 'a' : 'div';
  return (
    <Tag
      href={href}
      className="flex items-start gap-3 no-underline bg-white/[.04] border border-white/[.07] rounded-[14px] p-3 hover:bg-white/[.08] hover:border-white/15 hover:-translate-y-px transition-all duration-200"
    >
      <div className={`w-8 h-8 rounded-[10px] flex items-center justify-center flex-shrink-0 ${iconCls}`}>
        <Icon size={14} />
      </div>
      <div>
        <span className="block text-[8.5px] font-extrabold tracking-[.22em] uppercase text-white/25 mb-1">
          {label}
        </span>
        <div className="text-[13px] font-bold text-white/75 leading-snug">
          {children}
        </div>
      </div>
    </Tag>
  );
}

function HourCard({ icon, labelJa, labelEn, timeJa, timeEn, subJa, subEn, accent, isJa }) {
  return (
    <div className={[
      'relative rounded-[18px] p-4 border-[1.5px] overflow-hidden',
      accent
        ? 'bg-gradient-to-br from-[#284f22] to-[#173116] border-white/[.12] shadow-[0_4px_0_#0f2a0a]'
        : 'bg-white/[.04] border-white/[.1]',
    ].join(' ')}>
      <span className="absolute top-3.5 right-4 text-[22px] opacity-40 select-none pointer-events-none">
        {icon}
      </span>
      <p className={`text-[9px] font-extrabold tracking-[.22em] uppercase mb-1.5 ${accent ? 'text-[rgba(255,216,102,.5)]' : 'text-white/40'}`}>
        {isJa ? labelJa : labelEn}
      </p>
      <p className={`font-bold text-[26px] leading-none mb-1 ${accent ? 'text-[#ffd76a]' : 'text-white'}`}>
        {isJa ? timeJa : timeEn}
      </p>
      <p className="text-[10px] font-bold text-white/40">
        {isJa ? subJa : subEn}
      </p>
    </div>
  );
}

export default function Footer() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const isJa = lang?.startsWith('ja');

  const [privacyOpen, setPrivacyOpen] = useState(false);

  const scrollTo = (id) =>
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });

  const navLabel = (key) => {
    const fallback = {
      experiences: isJa ? '体験・アクティビティ' : 'Activities',
      food:        isJa ? 'お食事'               : 'Dining',
      access:      isJa ? 'アクセス'             : 'Access',
      faq:         isJa ? 'よくある質問'         : 'FAQ',
      reservation: isJa ? 'ご予約'               : 'Reservations',
    };
    try {
      const v = t(`nav.${key}`);
      return v !== `nav.${key}` ? v : fallback[key];
    } catch {
      return fallback[key];
    }
  };

  const copyright = (() => {
    try {
      const v = t('footer.copy');
      return v !== 'footer.copy'
        ? v
        : `© ${new Date().getFullYear()} 掛川バギー＆アウトドアパーク. All rights reserved.`;
    } catch {
      return `© ${new Date().getFullYear()} 掛川バギー＆アウトドアパーク. All rights reserved.`;
    }
  })();

  return (
    <>
      <footer className="relative overflow-hidden bg-[#0d1f0b]">

        {/* ══ SKY → FOREST HEADER ══ */}
        <div
          className="relative h-[160px] overflow-hidden"
          style={{ background: 'linear-gradient(180deg,#c8eaff 0%,#d8f0c8 40%,#23431f 100%)' }}
        >
          <div
            className="absolute top-[18px] right-[12%] w-[60px] h-[60px] rounded-full bg-[#ffe066]"
            style={{ boxShadow: '0 0 0 10px rgba(255,224,102,.18),0 0 0 24px rgba(255,224,102,.07)' }}
          />
          <svg
            className="absolute bottom-0 left-0 w-full h-[80px]"
            viewBox="0 0 1440 80"
            preserveAspectRatio="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M0,60 L180,10 L360,45 L540,5 L720,40 L900,8 L1080,38 L1260,12 L1440,35 L1440,80 L0,80Z"
              fill="#173016" fillOpacity=".5"
            />
            <path
              d="M0,70 L120,35 L280,58 L440,20 L600,52 L760,15 L920,48 L1100,18 L1280,44 L1440,28 L1440,80 L0,80Z"
              fill="#0d1f0b"
            />
          </svg>
          <div className="absolute bottom-[-4px] left-0 right-0 flex justify-around items-end pointer-events-none">
            {TREES.map((tr, i) => (
              <motion.span
                key={i}
                className="leading-none inline-block select-none"
                style={{ fontSize: tr.size, transformOrigin: 'bottom center' }}
                animate={{ rotate: [-1.5, 1.5, -1.5] }}
                transition={{ duration: tr.dur, delay: tr.delay, repeat: Infinity, ease: 'easeInOut' }}
              >
                {tr.e}
              </motion.span>
            ))}
          </div>
        </div>

        {/* ══ MAIN GLASS CARD ══ */}
        <div className="relative z-10 max-w-7xl mx-auto px-6 -mt-11 pb-4">
          <div className="bg-white/[.04] backdrop-blur-2xl border-[2px] border-white/[.1] rounded-[28px] shadow-[0_20px_64px_rgba(0,0,0,.35)] overflow-hidden relative">

            {/* Rainbow stripe */}
            <div
              className="absolute top-0 left-0 right-0 h-[5px] z-10"
              style={{ background: 'repeating-linear-gradient(90deg,#d89c10 0,#d89c10 40px,#1f4d2b 40px,#1f4d2b 80px,#ea622a 80px,#ea622a 120px)' }}
            />

            {/* 4-column grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-8 p-10 md:p-12">

              {/* BRAND */}
              <div>
                <div className="inline-flex items-center gap-2 mb-4 bg-[#284c23] text-[#d7ffcf] border border-white/[.12] rounded-full px-4 py-2 text-[11px] font-extrabold shadow-[0_3px_0_#162a12]">
                  🌲 Kakegawa Outdoor Park
                </div>
                <div className="flex items-center gap-3 mb-4">
                  <motion.div
  animate={{ y: [0, -5, 0] }}
  transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
  className="w-[52px] h-[52px] rounded-[16px] flex-shrink-0 overflow-hidden shadow-[4px_4px_0_#0f2a0a] border border-white/[.15]"
>
  <img
    src="/images/logo.JPG"
    alt="Kakegawa Buggy"
    className="w-full h-full object-cover"
  />
</motion.div>
                  <div>
                    <p className="text-[15px] font-extrabold text-white leading-snug">
                      {isJa ? '掛川バギー＆' : 'Kakegawa Buggy &'}
                      <br />
                      {isJa ? 'アウトドアパーク' : 'Outdoor Park'}
                    </p>
                    <p className="text-[10px] text-white/45 font-semibold mt-0.5">
                      Kakegawa Buggy & Outdoor Park
                    </p>
                  </div>
                </div>
                <p className="text-[13px] text-white/60 leading-[1.85] mb-4">
                  {isJa
                    ? '静岡県掛川市の豊かな自然の中で、バギー・チェンソー・ナン焼きなど、ここでしか体験できないアドベンチャーを。'
                    : "Adventure experiences unique to Kakegawa's nature — buggy rides, chainsaw art, naan baking and more."}
                </p>
               
              </div>

              {/* NAV */}
              <div>
                <ColHead>{isJa ? 'サイトマップ' : 'Site Map'}</ColHead>
                <ul className="flex flex-col gap-0.5 list-none p-0 m-0">
                  {NAV.map(n => (
                    <li key={n.key}>
                      <button
                        onClick={() => scrollTo(n.id)}
                        className="group flex items-center gap-2.5 w-full text-left bg-transparent border-0 cursor-pointer text-[13px] font-bold text-white/55 hover:text-[#ffd76a] hover:translate-x-1 py-2 transition-all duration-200"
                      >
                        <span className="w-1.5 h-1.5 rounded-full bg-[#ffd76a] scale-0 group-hover:scale-100 transition-transform flex-shrink-0" />
                        {navLabel(n.key)}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>

              {/* CONTACT */}
              <div>
                <ColHead>{isJa ? '連絡先' : 'Contact'}</ColHead>
                <div className="flex flex-col gap-2.5">
                  <ContactItem
                    icon={FiPhone} iconCls="bg-[rgba(234,98,42,.18)] text-[#ffb08c]"
                    label="Phone" href="tel:0537269489"
                  >
                    <span className="text-[18px] text-[#ffd76a] tracking-wide font-bold">
                      0537-26-9489
                    </span>
                  </ContactItem>
                  <ContactItem
                    icon={FiMapPin} iconCls="bg-[rgba(77,141,255,.15)] text-[#8cb8ff]"
                    label="Address"
                  >
                    <span className="text-[12px] leading-[1.7]">
                      〒437-1435<br />静岡県掛川市上土方嶺向902
                    </span>
                  </ContactItem>
                  <ContactItem
                    icon={FiGlobe} iconCls="bg-[rgba(66,197,138,.15)] text-[#7effc0]"
                    label="Website"
                  >
                    kakegawabuggy.jp
                  </ContactItem>
                  <ContactItem
                    icon={FiInstagram} iconCls="bg-[rgba(200,80,160,.15)] text-[#f9a8d4]"
                    label="Instagram"
                  >
                    @kakegawabuggy
                  </ContactItem>
                </div>
              </div>

              {/* HOURS */}
              <div>
                <ColHead>{isJa ? '営業時間' : 'Business Hours'}</ColHead>
                <div className="flex flex-col gap-2.5">
                  <HourCard
                    icon="☀️"
                    labelJa="通常営業" labelEn="Regular Hours"
                    timeJa="9:30〜17:00" timeEn="9:30 AM – 5:00 PM"
                    subJa="最終受付 16:30" subEn="Last entry 4:30 PM"
                    accent isJa={isJa}
                  />
                  <HourCard
                    icon="❄️"
                    labelJa="冬季（11〜2月）" labelEn="Winter (Nov–Feb)"
                    timeJa="〜16:30" timeEn="Until 4:30 PM"
                    subJa="最終受付 16:00" subEn="Last entry 4:00 PM"
                    isJa={isJa}
                  />
                </div>
              </div>

            </div>
          </div>
        </div>

      {/* ══ BOTTOM BAR ══ */}
<div className="border-t border-white/[.06] mt-4 px-6 md:px-8 py-4 flex flex-col md:flex-row md:items-center md:justify-between gap-4">

  {/* Left */}
  <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
    
    <p className="text-[11px] text-white/30 tracking-[.04em]">
      {copyright}
    </p>

    <button
      type="button"
      onClick={() => setPrivacyOpen(true)}
      className="text-left text-[11px] text-[#ffd76a] hover:text-white transition-colors underline underline-offset-4 bg-transparent border-0 cursor-pointer"
    >
      {isJa ? 'プライバシーポリシー' : 'Privacy Policy'}
    </button>

  </div>

  {/* Right */}
  <div className="flex items-center gap-4 flex-wrap">
    
    <div className="flex items-center gap-1.5 text-[10px] font-bold tracking-[.18em] uppercase text-white/25">
      <span>📍</span>
      {isJa ? '静岡県掛川市' : 'Kakegawa, Shizuoka'}
    </div>

    <div className="hidden sm:block w-px h-3.5 bg-white/[.1]" />

    <span className="text-[10px] font-bold tracking-[.15em] uppercase text-[rgba(255,216,102,.35)]">
      kakegawabuggy.jp
    </span>

  </div>
</div>

      </footer>

      {/* Privacy Policy Modal */}
      <PrivacyPolicy open={privacyOpen} onClose={() => setPrivacyOpen(false)} />
    </>
  );
}